// Why the home has no new suggestions while the company's billing pauses usage (D74: no plan, no credits, or this
// member's monthly limit), and the way to the Usage page, which explains the details and holds the plan actions.
// Namespace `home-suggestions`; copy by DESIGN, terms matched to ns/usage.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  credits: 'Your company has used all its credits, so there are no new suggestions for now.',
  plan: 'Your company has no active plan, so there are no new suggestions for now.',
  limit: 'You’ve reached your monthly credit limit, so there are no new suggestions for now.',
  paused: 'Usage is paused, so there are no new suggestions for now.',
  seeUsage: 'See usage',
};
export type HomeSuggestionsKey = keyof typeof en;
type Copy = Record<HomeSuggestionsKey, string>;

const bg: Copy = {
  credits: 'Фирмата ви използва всичките си кредити, затова засега няма нови предложения.',
  plan: 'Фирмата ви няма активен план, затова засега няма нови предложения.',
  limit: 'Достигнахте месечния си лимит на кредити, затова засега няма нови предложения.',
  paused: 'Използването е спряно, затова засега няма нови предложения.',
  seeUsage: 'Вижте използването',
};
const cs: Copy = {
  credits: 'Vaše firma vyčerpala všechny kredity, takže zatím nejsou žádné nové návrhy.',
  plan: 'Vaše firma nemá aktivní plán, takže zatím nejsou žádné nové návrhy.',
  limit: 'Dosáhli jste měsíčního limitu kreditů, takže zatím nejsou žádné nové návrhy.',
  paused: 'Využívání je pozastaveno, takže zatím nejsou žádné nové návrhy.',
  seeUsage: 'Zobrazit využití',
};
const da: Copy = {
  credits: 'Virksomheden har brugt alle sine kreditter, så der er ingen nye forslag lige nu.',
  plan: 'Virksomheden har ingen aktiv plan, så der er ingen nye forslag lige nu.',
  limit: 'Du har nået din månedlige kreditgrænse, så der er ingen nye forslag lige nu.',
  paused: 'Forbruget er sat på pause, så der er ingen nye forslag lige nu.',
  seeUsage: 'Se forbrug',
};
const de: Copy = {
  credits:
    'Ihr Unternehmen hat alle Credits aufgebraucht, daher gibt es vorerst keine neuen Vorschläge.',
  plan: 'Ihr Unternehmen hat keinen aktiven Plan, daher gibt es vorerst keine neuen Vorschläge.',
  limit:
    'Sie haben Ihr monatliches Credit-Limit erreicht, daher gibt es vorerst keine neuen Vorschläge.',
  paused: 'Die Nutzung ist pausiert, daher gibt es vorerst keine neuen Vorschläge.',
  seeUsage: 'Nutzung ansehen',
};
const el: Copy = {
  credits:
    'Η εταιρεία σας χρησιμοποίησε όλες τις πιστώσεις της, οπότε προς το παρόν δεν υπάρχουν νέες προτάσεις.',
  plan: 'Η εταιρεία σας δεν έχει ενεργό πρόγραμμα, οπότε προς το παρόν δεν υπάρχουν νέες προτάσεις.',
  limit: 'Φτάσατε το μηνιαίο όριο πιστώσεών σας, οπότε προς το παρόν δεν υπάρχουν νέες προτάσεις.',
  paused: 'Η χρήση είναι σε παύση, οπότε προς το παρόν δεν υπάρχουν νέες προτάσεις.',
  seeUsage: 'Προβολή χρήσης',
};
const es: Copy = {
  credits: 'Su empresa ha usado todos sus créditos, así que por ahora no hay sugerencias nuevas.',
  plan: 'Su empresa no tiene ningún plan activo, así que por ahora no hay sugerencias nuevas.',
  limit: 'Ha alcanzado su límite mensual de créditos, así que por ahora no hay sugerencias nuevas.',
  paused: 'El uso está en pausa, así que por ahora no hay sugerencias nuevas.',
  seeUsage: 'Ver uso',
};
const et: Copy = {
  credits: 'Sinu ettevõte on kõik krediidid ära kasutanud, seega uusi soovitusi praegu pole.',
  plan: 'Sinu ettevõttel pole aktiivset paketti, seega uusi soovitusi praegu pole.',
  limit: 'Oled jõudnud oma kuise krediidilimiidini, seega uusi soovitusi praegu pole.',
  paused: 'Kasutus on peatatud, seega uusi soovitusi praegu pole.',
  seeUsage: 'Vaata kasutust',
};
const fi: Copy = {
  credits:
    'Yrityksesi on käyttänyt kaikki krediittinsä, joten uusia ehdotuksia ei toistaiseksi ole.',
  plan: 'Yritykselläsi ei ole aktiivista pakettia, joten uusia ehdotuksia ei toistaiseksi ole.',
  limit:
    'Olet saavuttanut kuukausittaisen krediittirajasi, joten uusia ehdotuksia ei toistaiseksi ole.',
  paused: 'Käyttö on keskeytetty, joten uusia ehdotuksia ei toistaiseksi ole.',
  seeUsage: 'Näytä käyttö',
};
const fr: Copy = {
  credits:
    'Votre entreprise a utilisé tous ses crédits : pas de nouvelles suggestions pour le moment.',
  plan: 'Votre entreprise n’a aucun forfait actif : pas de nouvelles suggestions pour le moment.',
  limit:
    'Vous avez atteint votre limite mensuelle de crédits : pas de nouvelles suggestions pour le moment.',
  paused: 'L’utilisation est suspendue : pas de nouvelles suggestions pour le moment.',
  seeUsage: 'Voir l’utilisation',
};
const ga: Copy = {
  credits:
    'D’úsáid do chuideachta a creidmheasanna go léir, mar sin níl moltaí nua ann faoi láthair.',
  plan: 'Níl plean gníomhach ag do chuideachta, mar sin níl moltaí nua ann faoi láthair.',
  limit: 'Shroich tú do theorainn mhíosúil creidmheasa, mar sin níl moltaí nua ann faoi láthair.',
  paused: 'Tá an úsáid ar sos, mar sin níl moltaí nua ann faoi láthair.',
  seeUsage: 'Féach ar an úsáid',
};
const hr: Copy = {
  credits: 'Vaša tvrtka potrošila je sve kredite, pa zasad nema novih prijedloga.',
  plan: 'Vaša tvrtka nema aktivan paket, pa zasad nema novih prijedloga.',
  limit: 'Dosegli ste mjesečno ograničenje kredita, pa zasad nema novih prijedloga.',
  paused: 'Korištenje je pauzirano, pa zasad nema novih prijedloga.',
  seeUsage: 'Prikaži potrošnju',
};
const hu: Copy = {
  credits: 'A vállalat az összes kreditjét felhasználta, ezért egyelőre nincsenek új javaslatok.',
  plan: 'A vállalatnak nincs aktív csomagja, ezért egyelőre nincsenek új javaslatok.',
  limit: 'Elérte a havi kreditkeretét, ezért egyelőre nincsenek új javaslatok.',
  paused: 'A használat szünetel, ezért egyelőre nincsenek új javaslatok.',
  seeUsage: 'Használat megtekintése',
};
const it: Copy = {
  credits:
    'La tua azienda ha usato tutti i suoi crediti, quindi per ora non ci sono nuovi suggerimenti.',
  plan: 'La tua azienda non ha un piano attivo, quindi per ora non ci sono nuovi suggerimenti.',
  limit:
    'Hai raggiunto il tuo limite mensile di crediti, quindi per ora non ci sono nuovi suggerimenti.',
  paused: 'L’utilizzo è in pausa, quindi per ora non ci sono nuovi suggerimenti.',
  seeUsage: 'Vedi utilizzo',
};
const lt: Copy = {
  credits: 'Jūsų įmonė išnaudojo visus kreditus, todėl kol kas naujų pasiūlymų nėra.',
  plan: 'Jūsų įmonė neturi aktyvaus plano, todėl kol kas naujų pasiūlymų nėra.',
  limit: 'Pasiekėte savo mėnesio kreditų limitą, todėl kol kas naujų pasiūlymų nėra.',
  paused: 'Naudojimas pristabdytas, todėl kol kas naujų pasiūlymų nėra.',
  seeUsage: 'Žiūrėti naudojimą',
};
const lv: Copy = {
  credits: 'Jūsu uzņēmums ir izmantojis visus kredītus, tāpēc pagaidām jaunu ieteikumu nav.',
  plan: 'Jūsu uzņēmumam nav aktīva plāna, tāpēc pagaidām jaunu ieteikumu nav.',
  limit: 'Sasniegts jūsu mēneša kredītu limits, tāpēc pagaidām jaunu ieteikumu nav.',
  paused: 'Lietojums ir apturēts, tāpēc pagaidām jaunu ieteikumu nav.',
  seeUsage: 'Skatīt lietojumu',
};
const mt: Copy = {
  credits:
    'Il-kumpanija tiegħek użat il-krediti kollha tagħha, għalhekk għalissa m’hemmx suġġerimenti ġodda.',
  plan: 'Il-kumpanija tiegħek m’għandhiex pjan attiv, għalhekk għalissa m’hemmx suġġerimenti ġodda.',
  limit:
    'Wasalt għal-limitu ta’ kull xahar tal-krediti tiegħek, għalhekk għalissa m’hemmx suġġerimenti ġodda.',
  paused: 'L-użu huwa sospiż, għalhekk għalissa m’hemmx suġġerimenti ġodda.',
  seeUsage: 'Ara l-użu',
};
const nl: Copy = {
  credits:
    'Je bedrijf heeft al zijn credits gebruikt, dus er zijn voorlopig geen nieuwe suggesties.',
  plan: 'Je bedrijf heeft geen actief plan, dus er zijn voorlopig geen nieuwe suggesties.',
  limit:
    'Je hebt je maandlimiet voor credits bereikt, dus er zijn voorlopig geen nieuwe suggesties.',
  paused: 'Het gebruik is gepauzeerd, dus er zijn voorlopig geen nieuwe suggesties.',
  seeUsage: 'Gebruik bekijken',
};
const pl: Copy = {
  credits: 'Twoja firma wykorzystała wszystkie kredyty, więc na razie nie ma nowych sugestii.',
  plan: 'Twoja firma nie ma aktywnego planu, więc na razie nie ma nowych sugestii.',
  limit: 'Osiągnięto Twój miesięczny limit kredytów, więc na razie nie ma nowych sugestii.',
  paused: 'Korzystanie jest wstrzymane, więc na razie nie ma nowych sugestii.',
  seeUsage: 'Zobacz wykorzystanie',
};
const pt: Copy = {
  credits:
    'A sua empresa utilizou todos os créditos, por isso, de momento, não há novas sugestões.',
  plan: 'A sua empresa não tem um plano ativo, por isso, de momento, não há novas sugestões.',
  limit: 'Atingiu o seu limite mensal de créditos, por isso, de momento, não há novas sugestões.',
  paused: 'A utilização está em pausa, por isso, de momento, não há novas sugestões.',
  seeUsage: 'Ver utilização',
};
const ro: Copy = {
  credits: 'Compania dvs. a folosit toate creditele, așa că deocamdată nu există sugestii noi.',
  plan: 'Compania dvs. nu are un plan activ, așa că deocamdată nu există sugestii noi.',
  limit: 'Ați atins limita lunară de credite, așa că deocamdată nu există sugestii noi.',
  paused: 'Utilizarea este suspendată, așa că deocamdată nu există sugestii noi.',
  seeUsage: 'Vedeți utilizarea',
};
const sk: Copy = {
  credits: 'Vaša firma vyčerpala všetky kredity, takže zatiaľ nie sú žiadne nové návrhy.',
  plan: 'Vaša firma nemá aktívny plán, takže zatiaľ nie sú žiadne nové návrhy.',
  limit: 'Dosiahli ste mesačný limit kreditov, takže zatiaľ nie sú žiadne nové návrhy.',
  paused: 'Využívanie je pozastavené, takže zatiaľ nie sú žiadne nové návrhy.',
  seeUsage: 'Zobraziť využitie',
};
const sl: Copy = {
  credits: 'Vaše podjetje je porabilo vse kredite, zato za zdaj ni novih predlogov.',
  plan: 'Vaše podjetje nima aktivnega paketa, zato za zdaj ni novih predlogov.',
  limit: 'Dosegli ste mesečno omejitev kreditov, zato za zdaj ni novih predlogov.',
  paused: 'Uporaba je zaustavljena, zato za zdaj ni novih predlogov.',
  seeUsage: 'Prikaži porabo',
};
const sv: Copy = {
  credits: 'Företaget har använt alla sina krediter, så det finns inga nya förslag just nu.',
  plan: 'Företaget har ingen aktiv plan, så det finns inga nya förslag just nu.',
  limit: 'Du har nått din månadsgräns för krediter, så det finns inga nya förslag just nu.',
  paused: 'Användningen är pausad, så det finns inga nya förslag just nu.',
  seeUsage: 'Visa användning',
};

export const homeSuggestionsCopy: NamespaceCatalog<HomeSuggestionsKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default homeSuggestionsCopy;
